import {
  Component,
  OnInit,
  inject,
  runInInjectionContext,
  effect,
  Injector,
  signal,
  DestroyRef,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { IonApp, IonContent, IonRouterOutlet, Platform } from '@ionic/angular/standalone';
import { OperatorService } from './core/services/operator/operator.service';
import { EventName } from './core/services/analytics/event-names';
import { AnalyticsService } from './core/services/analytics/analytics.service';
import { ThemeService } from './core/services/theme/theme.service';
import { InitService } from './core/services/init/init.service';
import { BootupService } from './core/services/bootup/bootup.service';
import { ActivityFeedService } from './core/services/activity-feed/activity-feed.service';
import { getLegacySafeAreaInsets } from './shared/utils/safe-area.utils';
import { InitialUrlService } from './core/services/initial-url/initial-url.service';
import { ResultsTab } from './core/services/theme/theme-config';
import { Theme } from './core/services/theme/theme-type';
import { TournamentService } from './core/services/tournament/tournament.service';
import { ClientEventService } from './core/services/analytics/client-event.service';
import { AppHeaderComponent } from './features/app-header/app-header.component';
import { NgClass } from '@angular/common';
import { ScrollStateService } from './core/services/scroll-state/scroll-state.service';
import { GameHostComponent } from './features/game/game-host/game-host.component';
import { GameOrchestrationService } from './core/services/game-orchestration/game-orchestration.service';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [IonApp, IonContent, IonRouterOutlet, NgClass, AppHeaderComponent, GameHostComponent],
})
export class AppComponent implements OnInit {
  @ViewChild(IonContent) content!: IonContent;

  private router = inject(Router);
  private platform = inject(Platform);
  private initialUrlService = inject(InitialUrlService);
  private analyticsService = inject(AnalyticsService);
  private clientEventService = inject(ClientEventService);
  private injector = inject(Injector);
  private initService = inject(InitService);
  private bootupService = inject(BootupService);
  private activityFeedService = inject(ActivityFeedService);
  private operatorService = inject(OperatorService);
  private tournamentService = inject(TournamentService);
  private destroyRef = inject(DestroyRef);

  readonly scrollState = inject(ScrollStateService);
  readonly orchestration = inject(GameOrchestrationService);

  Theme = Theme;
  themeService = inject(ThemeService);
  recentMessages = signal<string[]>([]);

  constructor() {
    this.applySafeAreaInsets();

    window.addEventListener('error', (event) => {
      const err = event.error;

      // Prevent Phaser error from propogating to the console or Sentry
      if (
        err instanceof DOMException &&
        err.name === 'SecurityError' &&
        err.message.includes('removeEventListener')
      ) {
        event.preventDefault(); // stops it from being reported to console
        event.stopImmediatePropagation(); // blocks Angular from handling it
      }
    });

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        this.scrollState.handleNavigationEnd(e.urlAfterRedirects, this.content);
      });
  }

  private async initialise() {
    if (!OperatorService.IsDemo) {
      this.analyticsService.initialise(this.initialUrlService.operatorData);
      this.clientEventService.initialise(this.initialUrlService.operatorData);
    }

    // Preload the bootup data in parallel to signing the user in
    this.bootupService.preload();

    this.analyticsService.log(EventName.FirebaseInitialised);

    // Move initService.initialise() here, after Firebase is ready
    this.initService.initialise();

    runInInjectionContext(this.injector, () => {
      effect(async () => {
        const initialised = this.initService.initialised();
        if (initialised) {
          try {
            const tabOrder = this.themeService.resultsTabOrder() ?? [
              ResultsTab.results,
              ResultsTab.worldwide,
              ResultsTab.mystats,
            ];
            await Promise.all([
              ...(tabOrder.includes(ResultsTab.worldwide)
                ? [this.activityFeedService.initialize()]
                : []),
              this.tournamentService.initialize(),
            ]);
          } catch (error) {
            console.error(error);
          }
        }
      });
    });
  }

  async ngOnInit(): Promise<void> {
    await this.initialise();
  }

  onCloseClicked() {
    this.analyticsService.log(EventName.RedirectToOperator);
    this.operatorService.redirectToOperator();
  }

  get hasAnyBackgroundImg(): boolean {
    return this.themeService.hasBackgroundImg() || this.hasBackgroundImgForGame;
  }

  get backgroundImage(): string {
    const layers: string[] = [];

    if (this.hasBackgroundImgForGame) {
      layers.push(
        `url('${this.themeService.backgroundImgUrlForGame(this.operatorService.gameId)}')`,
      );
    }

    if (this.themeService.hasBackgroundImg()) {
      layers.push(`url('${this.themeService.backgroundImgUrl()}')`);
    }

    return layers.length > 0 ? layers.join(', ') : `none`;
  }

  readonly drawerTitleStyle: [string, string][] = [
    ['type', 'drawer-title'],
    ['text-color', 'drawer-title'],
  ];

  async canDismiss(data?: unknown, role?: string) {
    return role !== 'gesture';
  }

  onHeaderCloseClicked() {
    this.onCloseClicked();
  }

  onHeaderTabSelected(tabId: string) {
    this.router.navigate([tabId]);
  }

  private get hasBackgroundImgForGame(): boolean {
    return this.themeService.hasBackgroundImgForGame(this.operatorService.gameId);
  }

  private applySafeAreaInsets(): void {
    this.platform.ready().then(async () => {
      try {
        const legacyInsets = getLegacySafeAreaInsets();

        const top = this.initialUrlService.insetTop ?? legacyInsets.top;
        const bottom = this.initialUrlService.insetBottom ?? legacyInsets.bottom;
        const left = this.initialUrlService.insetLeft ?? legacyInsets.left;
        const right = this.initialUrlService.insetRight ?? legacyInsets.right;

        const root = document.documentElement;

        root.style.setProperty('--native-safe-area-inset-top', `${top}px`);
        root.style.setProperty('--native-safe-area-inset-bottom', `${bottom}px`);
        root.style.setProperty('--native-safe-area-inset-left', `${left}px`);
        root.style.setProperty('--native-safe-area-inset-right', `${right}px`);

        root.style.setProperty('--ion-safe-area-top', `${top}px`);
        root.style.setProperty('--ion-safe-area-bottom', `${bottom}px`);
        root.style.setProperty('--ion-safe-area-left', `${left}px`);
        root.style.setProperty('--ion-safe-area-right', `${right}px`);
      } catch (error) {
        this.analyticsService.handleError(error, 'Set safe area insets');
      }
    });
  }
}
